import { motion } from "framer-motion";

/**
 * Reveal wrapper: fades + rises into view once when scrolled into viewport.
 * Compatible with Lenis (framer-motion detects scroll via IntersectionObserver).
 */
export function Reveal({ children, delay = 0, y = 40, duration = 0.9, className, as = "div" }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px 0px -80px 0px" }}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </MotionTag>
  );
}

/**
 * StaggerGroup + StaggerItem for lists that reveal in sequence.
 */
export function StaggerGroup({ children, className, stagger = 0.08, delay = 0 }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px 0px" }}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className, y = 30 }) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y },
        show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * MaskedLine: word-by-word masked reveal for editorial headlines.
 * Splits the text on spaces and lifts each word from below a mask.
 */
export function MaskedLine({ text, className, delay = 0, wordDelay = 0.05 }) {
  const words = text.split(" ");
  return (
    <span className={className} aria-label={text}>
      {words.map((w, i) => (
        <span
          key={i}
          className="inline-block overflow-hidden align-baseline"
          style={{ marginRight: "0.28em" }}
          aria-hidden
        >
          <motion.span
            className="inline-block will-change-transform"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{
              duration: 0.9,
              ease: [0.2, 0.9, 0.2, 1],
              delay: delay + i * wordDelay,
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}
